// apps/web/src/components/painel/home/perfil-tabs.tsx — Fase 17.8, Plano 04 (D-01, D-02, D-04; UI-SPEC U-02).
// Seletor de perfil do /painel sobre o Tabs do Radix: tablist com ativação automática (setas, Home/End), só as abas
// permitidas e nenhum seletor com uma aba só. O componente só notifica a troca; quem grava o cookie é a página.
// Plano 09 (U-02): a partir de 1280px o seletor sobe para o slot da topbar; abaixo disso (ou sem alvo) fica na primeira
// linha da página, com margem inferior de 16px.
import { useEffect, useState, type ReactElement } from 'react';
import type { PerfilHome } from '@pieltec/contracts';
import { ROTULO_ABA } from '../../../lib/painel/home/abas';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { PreencherSlotTopbar, useTemAlvoSlotTopbar } from '../../shell/topbar-slot';
import '../../../styles/painel-home.css';

/** Id do painel de conteúdo da aba ativa (a página o usa em `role="tabpanel"`). */
export const ID_PAINEL_DA_ABA = 'painel-aba-conteudo';

/** Id do gatilho de cada aba (alvo do `aria-labelledby` do painel). */
export const idDaAba = (perfil: PerfilHome): string => `painel-aba-gatilho-${perfil}`;

export interface PerfilTabsProps {
  readonly abas: readonly PerfilHome[];
  readonly ativa: PerfilHome;
  readonly onTrocar: (perfil: PerfilHome) => void;
}

const CONSULTA_LARGA = '(min-width: 1280px)';

/** `true` a partir de 1280px (mesmo corte do shell); sem `matchMedia` (testes isolados) vale `false`. */
function useTelaLarga(): boolean {
  const [larga, setLarga] = useState<boolean>(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(CONSULTA_LARGA).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const consulta = window.matchMedia(CONSULTA_LARGA);
    const atualizar = (): void => setLarga(consulta.matches);
    atualizar();
    consulta.addEventListener('change', atualizar);
    return () => consulta.removeEventListener('change', atualizar);
  }, []);
  return larga;
}

export function PerfilTabs({ abas, ativa, onTrocar }: PerfilTabsProps): ReactElement | null {
  const larga = useTelaLarga();
  const temAlvo = useTemAlvoSlotTopbar();
  if (abas.length <= 1) return null;
  const seletor = (
    <Tabs value={ativa} onValueChange={(valor) => onTrocar(valor as PerfilHome)} className="painel-abas-raiz">
      <TabsList data-testid="painel-abas" aria-label="Ver painel como perfil" className="painel-abas">
        {abas.map((perfil) => (
          <TabsTrigger
            key={perfil}
            value={perfil}
            id={idDaAba(perfil)}
            aria-controls={ID_PAINEL_DA_ABA}
            data-perfil={perfil}
            data-testid={`painel-aba-${perfil}`}
            data-texto={ROTULO_ABA[perfil]}
            className="painel-aba"
          >
            {ROTULO_ABA[perfil]}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
  return larga && temAlvo ? <PreencherSlotTopbar>{seletor}</PreencherSlotTopbar> : seletor;
}
